import React, {
  useEffect,
  useState,
} from "react";

import axios from "axios";

import "./LeaderboardManager.css";

const LeaderboardManager = () => {

  // =====================================================
  // BASIC STATES
  // =====================================================

  const [loading, setLoading] =
    useState(false);

  const [creating, setCreating] =
    useState(false);

  const [currentToken, setCurrentToken] =
    useState("");

  const [createdAt, setCreatedAt] =
    useState("");

  const [copied, setCopied] =
    useState(false);

  const [showConfirm, setShowConfirm] =
    useState(false);


  // =====================================================
  // WHATSAPP STATES
  // =====================================================

  const [
    visionIgniteWhatsappLink,
    setVisionIgniteWhatsappLink,
  ] = useState("");

  const [
    igniteWhatsappLink,
    setIgniteWhatsappLink,
  ] = useState("");

  const [savingLink, setSavingLink] =
    useState(false);


  // =====================================================
  // OFFER STATES
  // =====================================================

  const [offers, setOffers] =
    useState([]);

  const [offerLoading, setOfferLoading] =
    useState(false);

  const [
    offerActionLoading,
    setOfferActionLoading,
  ] = useState("");


  // =====================================================
  // ADMIN AUTH STATES
  // =====================================================

  const [authorized, setAuthorized] =
    useState(false);

  const [password, setPassword] =
    useState("");

  const [
    passwordLoading,
    setPasswordLoading,
  ] = useState(false);


  // =====================================================
  // CHECK EXISTING LOGIN
  // Same method as WhatsappCRM
  // =====================================================

  useEffect(() => {

    const auth =
      localStorage.getItem(
        "whatsapp_admin"
      );

    if (auth === "true") {

      setAuthorized(true);

    }

  }, []);


  // =====================================================
  // ADMIN PASSWORD VERIFY
  // Same API as WhatsappCRM
  // =====================================================

  const verifyPassword =
    async () => {

      if (!password.trim()) {

        alert(
          "Please enter password."
        );

        return;

      }

      try {

        setPasswordLoading(true);

        const res =
          await axios.post(
            "/api/admin-login",
            {
              password,
            }
          );

        if (
          res.data?.success
        ) {

          localStorage.setItem(
            "whatsapp_admin",
            "true"
          );

          setAuthorized(true);

          setPassword("");

        }

      }
      catch (error) {

        console.error(
          "❌ Admin login error:",
          error
        );

        alert(
          error.response?.data?.message ||
          "Wrong Password"
        );

      }
      finally {

        setPasswordLoading(
          false
        );

      }

    };


  // =====================================================
  // LOGOUT
  // =====================================================

  const handleLogout = () => {

    localStorage.removeItem(
      "whatsapp_admin"
    );

    setAuthorized(false);

  };


  // =====================================================
  // FETCH OFFERS
  // =====================================================

  const fetchOffers =
    async () => {

      try {

        setOfferLoading(true);

        const response =
          await axios.get(
            "/api/admin/offers"
          );

        if (
          response.data?.success
        ) {

          setOffers(
            response.data.offers ||
            []
          );

        }

      }
      catch (error) {

        console.error(
          "❌ Fetch offers error:",
          error
        );

      }
      finally {

        setOfferLoading(false);

      }

    };


  // =====================================================
  // FETCH CURRENT DATA
  // =====================================================

  const fetchCurrentLink =
    async () => {

      try {

        setLoading(true);


        // ===============================================
        // LEADERBOARD
        // ===============================================

        const res =
          await axios.get(
            "/api/admin/current-leaderboard-link"
          );

        if (
          res.data.success
        ) {

          setCurrentToken(
            res.data.token ||
            ""
          );

          setCreatedAt(
            res.data.createdAt ||
            ""
          );

        }


        // ===============================================
        // WHATSAPP
        // ===============================================

        const webinarRes =
          await axios.get(
            "/api/webinar-settings"
          );

        setVisionIgniteWhatsappLink(
          webinarRes.data
            .visionIgniteWhatsappLink ||
          ""
        );

        setIgniteWhatsappLink(
          webinarRes.data
            .igniteWhatsappLink ||
          ""
        );


        // ===============================================
        // OFFERS
        // ===============================================

        await fetchOffers();

      }
      catch (error) {

        console.error(
          "❌ Fetch manager data error:",
          error
        );

      }
      finally {

        setLoading(false);

      }

    };


  // =====================================================
  // LOAD DATA ONLY AFTER ADMIN LOGIN
  // =====================================================

  useEffect(() => {

    if (authorized) {

      fetchCurrentLink();

    }

  }, [authorized]);


  // =====================================================
  // COPY LEADERBOARD LINK
  // =====================================================

  const handleCopy =
    async () => {

      if (!currentToken) {

        return;

      }

      const fullLink =
        `${window.location.origin}/public-leaderboard/${currentToken}`;

      try {

        await navigator.clipboard.writeText(
          fullLink
        );

        setCopied(true);

        setTimeout(() => {

          setCopied(false);

        }, 2000);

      }
      catch (error) {

        console.error(
          "❌ Copy error:",
          error
        );

        alert(
          "Unable to copy link."
        );

      }

    };


  // =====================================================
  // SAVE WHATSAPP LINKS
  // =====================================================

  const saveWhatsappLink =
    async () => {

      try {

        setSavingLink(true);

        await axios.put(
          "/api/webinar-settings",
          {
            visionIgniteWhatsappLink,
            igniteWhatsappLink,
          }
        );

        alert(
          "WhatsApp links updated successfully"
        );

      }
      catch (error) {

        console.error(
          "❌ WhatsApp update error:",
          error
        );

        alert(
          error.response?.data?.message ||
          "Update failed"
        );

      }
      finally {

        setSavingLink(false);

      }

    };


  // =====================================================
  // GENERATE NEW LEADERBOARD LINK
  // =====================================================

  const generateNewLink =
    async () => {

      try {

        setCreating(true);

        const res =
          await axios.post(
            "/api/admin/create-leaderboard-link"
          );

        if (
          res.data.success
        ) {

          setCurrentToken(
            res.data.token
          );

          setCreatedAt(
            new Date().toISOString()
          );

        }

        setShowConfirm(false);

      }
      catch (error) {

        console.error(
          "❌ Generate link error:",
          error
        );

        alert(
          error.response?.data?.message ||
          "Unable to generate new link."
        );

      }
      finally {

        setCreating(false);

      }

    };


  // =====================================================
  // FIND OFFER
  // =====================================================

  const getOfferByCode =
    (code) => {

      return offers.find(
        (offer) =>
          offer.code === code
      );

    };


  // =====================================================
  // ACTIVATE OFFER
  // =====================================================

  const activateOffer =
    async (code) => {

      try {

        setOfferActionLoading(
          `activate-${code}`
        );

        const response =
          await axios.patch(
            `/api/admin/offers/${encodeURIComponent(
              code
            )}/activate`
          );

        if (
          response.data?.success
        ) {

          await fetchOffers();

          alert(
            "Offer activated successfully."
          );

        }

      }
      catch (error) {

        console.error(
          "❌ Activate offer error:",
          error
        );

        alert(
          error.response?.data?.message ||
          "Unable to activate offer."
        );

      }
      finally {

        setOfferActionLoading("");

      }

    };


  // =====================================================
  // DEACTIVATE OFFER
  // =====================================================

  const deactivateOffer =
    async (code) => {

      try {

        setOfferActionLoading(
          `deactivate-${code}`
        );

        const response =
          await axios.patch(
            `/api/admin/offers/${encodeURIComponent(
              code
            )}/deactivate`
          );

        if (
          response.data?.success
        ) {

          await fetchOffers();

          alert(
            "Offer deactivated successfully."
          );

        }

      }
      catch (error) {

        console.error(
          "❌ Deactivate offer error:",
          error
        );

        alert(
          error.response?.data?.message ||
          "Unable to deactivate offer."
        );

      }
      finally {

        setOfferActionLoading("");

      }

    };


  // =====================================================
  // OFFER CARD
  // =====================================================

  const renderOfferCard = (
    code,
    title,
    icon
  ) => {

    const offer =
      getOfferByCode(code);


    // -----------------------------------------------
    // OFFER NOT FOUND
    // -----------------------------------------------

    if (!offer) {

      return (

        <div
          className="current-link-box"
        >

          <h3>
            {icon} {title}
          </h3>

          <p>
            Offer not found in database.
          </p>

        </div>

      );

    }


    // -----------------------------------------------
    // PRICE
    // -----------------------------------------------

    const normalPrice =
      Number(
        offer.originalAmount
      ) / 100;

    const offerPrice =
      Number(
        offer.offerAmount
      ) / 100;


    // -----------------------------------------------
    // LOADING
    // -----------------------------------------------

    const activateLoading =
      offerActionLoading ===
      `activate-${offer.code}`;

    const deactivateLoading =
      offerActionLoading ===
      `deactivate-${offer.code}`;


    return (

      <div
        className="current-link-box"
      >

        <h3>
          {icon} {title}
        </h3>


        <p>
          Normal Price:{" "}

          <strong>
            ₹
            {normalPrice.toLocaleString(
              "en-IN"
            )}
          </strong>

        </p>


        <p>
          Offer Price:{" "}

          <strong>
            ₹
            {offerPrice.toLocaleString(
              "en-IN"
            )}
          </strong>

        </p>


        <p>
          Status:{" "}

          <strong
            style={{
              color:
                offer.isActive
                  ? "#16a34a"
                  : "#dc2626",
            }}
          >
            {offer.isActive
              ? "ACTIVE"
              : "INACTIVE"}
          </strong>

        </p>


        <div
          className="manager-buttons"
        >

          {offer.isActive ? (

            <button
              type="button"
              className="copy-btn"
              onClick={() =>
                deactivateOffer(
                  offer.code
                )
              }
              disabled={
                deactivateLoading
              }
            >

              {deactivateLoading
                ? "Deactivating..."
                : "🔴 Deactivate"}

            </button>

          ) : (

            <button
              type="button"
              className="generate-btn"
              onClick={() =>
                activateOffer(
                  offer.code
                )
              }
              disabled={
                activateLoading
              }
            >

              {activateLoading
                ? "Activating..."
                : "🟢 Activate ₹7,999"}

            </button>

          )}

        </div>

      </div>

    );

  };


  // =====================================================
  // ADMIN LOGIN PAGE
  // Same style/logic as WhatsappCRM
  // =====================================================

  if (!authorized) {

    return (

      <div className="admin-login">

        <div className="admin-login-card">

          <h2>
            🏆 Leaderboard Manager
          </h2>

          <p>
            Enter admin password to continue.
          </p>


          <input
            type="password"
            placeholder="Enter Password"
            value={password}
            onChange={(e) =>
              setPassword(
                e.target.value
              )
            }
            onKeyDown={(e) => {

              if (
                e.key === "Enter"
              ) {

                verifyPassword();

              }

            }}
            disabled={
              passwordLoading
            }
          />


          <button
            onClick={
              verifyPassword
            }
            disabled={
              passwordLoading
            }
          >

            {passwordLoading
              ? "Checking..."
              : "Login"}

          </button>

        </div>

      </div>

    );

  }


  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {

    return (

      <div className="manager-loading">

        <div className="manager-spinner"></div>

        <h2>
          Loading...
        </h2>

      </div>

    );

  }


  // =====================================================
  // MAIN PAGE
  // =====================================================

  return (

    <div className="leaderboard-manager">


      {/* =================================================
          LOGOUT
      ================================================= */}

      <div
        style={{
          width: "100%",
          maxWidth: "750px",
          display: "flex",
          justifyContent: "flex-end",
          marginBottom: "15px",
        }}
      >

        <button
          type="button"
          onClick={
            handleLogout
          }
          style={{
            padding:
              "9px 16px",

            border:
              "none",

            borderRadius:
              "7px",

            background:
              "#dc2626",

            color:
              "#fff",

            cursor:
              "pointer",

            fontWeight:
              "600",
          }}
        >

          🔒 Logout

        </button>

      </div>


      {/* =================================================
          LEADERBOARD MANAGER
      ================================================= */}

      <div className="manager-card">

        <h1>
          🏆 Leaderboard Manager
        </h1>

        <p>
          Generate and manage the public
          leaderboard link.
        </p>


        <div className="current-link-box">

          <h3>
            Current Active Link
          </h3>


          {currentToken ? (

            <input
              type="text"
              readOnly
              value={`${window.location.origin}/leaderboard/${currentToken}`}
            />

          ) : (

            <p>
              No active link available.
            </p>

          )}


          {createdAt && (

            <small>

              Generated On :{" "}

              {new Date(
                createdAt
              ).toLocaleString()}

            </small>

          )}

        </div>


        <div
          className="manager-buttons"
        >

          <button
            type="button"
            className="copy-btn"
            onClick={
              handleCopy
            }
            disabled={
              !currentToken
            }
          >

            {copied
              ? "✅ Copied"
              : "📋 Copy Link"}

          </button>


          <button
            type="button"
            className="generate-btn"
            onClick={() =>
              setShowConfirm(
                true
              )
            }
            disabled={
              creating
            }
          >

            {creating
              ? "Generating..."
              : "🔄 Generate New Link"}

          </button>

        </div>

      </div>


      {/* =================================================
          CONFIRM MODAL
      ================================================= */}

      {showConfirm && (

        <div className="confirm-overlay">

          <div className="confirm-box">

            <h2>
              Generate New Link?
            </h2>

            <p>
              The current leaderboard
              link will expire immediately.
            </p>


            <div
              className="confirm-buttons"
            >

              <button
                type="button"
                className="cancel-btn"
                onClick={() =>
                  setShowConfirm(
                    false
                  )
                }
              >

                Cancel

              </button>


              <button
                type="button"
                className="confirm-btn"
                onClick={
                  generateNewLink
                }
                disabled={
                  creating
                }
              >

                {creating
                  ? "Generating..."
                  : "Generate"}

              </button>

            </div>

          </div>

        </div>

      )}


      {/* =================================================
          OFFER MANAGER
      ================================================= */}

      <div className="manager-card">

        <h2>
          💰 Offer Manager
        </h2>

        <p>
          Manage Ignite and Vision Ignite
          ₹7,999 offers independently.
        </p>


        {offerLoading ? (

          <p>
            Loading offers...
          </p>

        ) : (

          <>

            {renderOfferCard(
              "webinar7999",
              "Forge Your Peak - Ignite",
              "🔥"
            )}


            {renderOfferCard(
              "vision_ignite7999",
              "Forge Your Peak - Vision Ignite",
              "👁️"
            )}

          </>

        )}

      </div>


      {/* =================================================
          WHATSAPP SETTINGS
      ================================================= */}

      <div className="manager-card">

        <h2>
          📱 Webinar WhatsApp Settings
        </h2>

        <p>
          Manage WhatsApp community links
          for each webinar.
        </p>


        {/* ==============================================
            VISION IGNITE
        ============================================== */}

        <div
          className="current-link-box"
        >

          <h3>
            Vision Ignite WhatsApp Community
          </h3>


          <input
            type="text"
            value={
              visionIgniteWhatsappLink
            }
            onChange={(e) =>
              setVisionIgniteWhatsappLink(
                e.target.value
              )
            }
            placeholder="Paste Vision Ignite WhatsApp Invite Link"
            disabled={
              savingLink
            }
          />

        </div>


        {/* ==============================================
            IGNITE
        ============================================== */}

        <div
          className="current-link-box"
        >

          <h3>
            Ignite Webinar WhatsApp Community
          </h3>


          <input
            type="text"
            value={
              igniteWhatsappLink
            }
            onChange={(e) =>
              setIgniteWhatsappLink(
                e.target.value
              )
            }
            placeholder="Paste Ignite Webinar WhatsApp Invite Link"
            disabled={
              savingLink
            }
          />

        </div>


        <div
          className="manager-buttons"
        >

          <button
            type="button"
            className="generate-btn"
            onClick={
              saveWhatsappLink
            }
            disabled={
              savingLink
            }
          >

            {savingLink
              ? "Saving..."
              : "💾 Save WhatsApp Links"}

          </button>

        </div>

      </div>

    </div>

  );

};

export default LeaderboardManager;